import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Vault } from "lucide-react";

const links = [
  { to: "/", label: "Home", id: "nav-link-landing" },
  { to: "/about", label: "About", id: "nav-link-about" },
];

const trailingLinks = [
  { to: "/faq", label: "FAQ", id: "nav-link-faq" },
  { to: "/contact", label: "Contact", id: "nav-link-contact" },
  { to: "/book", label: "Book A Call", id: "nav-link-book" },
];

const fundingLinks = [
  { to: "/products/artist-capital-loans", label: "Artist Capital Loans", id: "nav-link-artist-capital-loans" },
  { to: "/products/label-lines-of-credit", label: "Label Lines of Credit", id: "nav-link-label-lines-of-credit" },
  { to: "/products/business-credit-cards", label: "Business Credit Cards", id: "nav-link-business-credit-cards" },
  { to: "/products/catalog-advances", label: "Catalog Advances", id: "nav-link-catalog-advances" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[#413830]/95 backdrop-blur-md border-b border-[rgba(212,175,55,0.35)]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <span className="w-9 h-9 border border-gold/60 flex items-center justify-center gold-border-glow group-hover:border-gold transition-colors">
            <Vault className="w-4 h-4 text-gold" />
          </span>
          <span className="font-serif text-lg tracking-[0.18em] text-[#f3e5ab]">
            EMISON <span className="gold-text">MANAGEMENT</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={`text-xs font-serif uppercase tracking-[0.2em] transition-colors hover:text-gold ${
                pathname === l.to ? "text-gold" : "text-[#d8cdb8]"
              }`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="relative group" data-testid="nav-funding-dropdown">
            <button
              data-testid="nav-funding-toggle"
              className={`text-xs font-serif uppercase tracking-[0.2em] transition-colors hover:text-gold ${
                pathname.startsWith("/products") ? "text-gold" : "text-[#d8cdb8]"
              }`}
            >
              Funding
            </button>
            <div className="absolute right-0 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
              <div className="relative bg-[#fffdf9] border border-[#e3d3ac] shadow-[0_25px_60px_rgba(45,26,11,0.3)] min-w-[240px] py-2">
                <span
                  className="absolute bottom-0 left-0 right-0 h-[3px]"
                  style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                />
                {fundingLinks.map((f) => (
                  <NavLink
                    key={f.to}
                    to={f.to}
                    data-testid={f.id}
                    className={({ isActive }) =>
                      `block px-6 py-3 text-xs font-serif uppercase tracking-[0.15em] transition-colors ${
                        isActive ? "text-gold" : "text-[#4D4232] hover:text-gold"
                      }`
                    }
                  >
                    {f.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
          {trailingLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={`text-xs font-serif uppercase tracking-[0.2em] transition-colors hover:text-gold ${
                pathname === l.to ? "text-gold" : "text-[#d8cdb8]"
              }`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/apply"
            data-testid="nav-link-apply"
            className="sheen-btn bg-gold text-[#4D4232] text-xs font-semibold uppercase tracking-[0.2em] px-6 py-3 hover:bg-gold-bright transition-colors"
          >
            Go Backstage
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-[#f3e5ab]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[rgba(212,175,55,0.25)] bg-[#413830] px-6 py-6 space-y-5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.id}-mobile`}
              onClick={() => setOpen(false)}
              className="block text-sm font-serif uppercase tracking-[0.2em] text-[#d8cdb8] hover:text-gold"
            >
              {l.label}
            </NavLink>
          ))}
          <p className="text-[10px] font-serif uppercase tracking-[0.3em] text-gold pt-2">Funding</p>
          {fundingLinks.map((f) => (
            <NavLink
              key={f.to}
              to={f.to}
              data-testid={`${f.id}-mobile`}
              onClick={() => setOpen(false)}
              className="block pl-4 text-sm font-serif uppercase tracking-[0.2em] text-[#d8cdb8] hover:text-gold"
            >
              {f.label}
            </NavLink>
          ))}
          {trailingLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.id}-mobile`}
              onClick={() => setOpen(false)}
              className="block text-sm font-serif uppercase tracking-[0.2em] text-[#d8cdb8] hover:text-gold"
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/apply"
            data-testid="nav-link-apply-mobile"
            onClick={() => setOpen(false)}
            className="block bg-gold text-[#4D4232] text-xs font-semibold uppercase tracking-[0.2em] px-6 py-3 text-center"
          >
            Go Backstage
          </Link>
        </div>
      )}
    </header>
  );
};

export default Navbar;
